import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PauseCircle, PlayCircle, Clock, X, ChevronLeft, ChevronRight, Info } from "lucide-react";

export default function Campaigns() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/campaigns/overview").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const suppressAddress = async () => {
    const email = window.prompt("Mark which address as inactive?\n\nUse for auto-replies like \u201cThis mailbox is no longer active.\u201d Cancels every unsent touch to it (drips, drafts, follow-ups).");
    if (!email || !email.includes("@")) return;
    try {
      const { data } = await api.post("/admin/suppress-email", { email, reason: "manual_admin" });
      toast.success(`${data.email} suppressed — ${data.queued_cancelled} queued touch${data.queued_cancelled === 1 ? "" : "es"} cancelled`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Suppress failed"); }
  };

  const verifyRecipients = async () => {
    if (!window.confirm("Re-verify all queued + recently-sent claim recipients (email syntax + domain DNS)? Failures get auto-suppressed. Takes up to a minute.")) return;
    toast.info("Re-verifying recipients…");
    try {
      const { data } = await api.post("/admin/verify-recipients?scope=both");
      toast.success(`Checked ${data.checked} addresses across ${data.unique_domains} domains — ${data.suppressed} suppressed (${data.invalid_syntax.length} bad syntax, ${data.dead_domains.length} dead domains)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
  };

  const retemplateQueue = async () => {
    if (!window.confirm("Rewrite every UNSENT claim pitch with the current personalized copy (named coach + school, one-button CTA)? Sent emails are untouched.")) return;
    try {
      const { data } = await api.post("/admin/claim-queue/retemplate");
      toast.success(`${data.updated} queued pitches rewritten with the new copy`);
    } catch (e) { toast.error(e.response?.data?.detail || "Rewrite failed"); }
  };

  return (
    <div className="px-4 md:px-8 py-8 max-w-6xl" data-testid="hq-campaigns-page">
      <div className="text-xs tracking-widest text-volt">ADMIN HQ · OPERATIONS</div>
      <h1 className="heading text-4xl mt-1">CAMPAIGNS</h1>
      <p className="text-neutral-400 mt-2 text-sm max-w-2xl">
        Three separate outbound campaigns. Nothing sends because a page was opened or refreshed —
        campaigns run on a daily schedule only while switched on, and every start requires an explicit confirmation.
      </p>
      <div className="mt-4 flex gap-2 flex-wrap">
        <button data-testid="suppress-address-btn" onClick={suppressAddress}
          className="min-h-[38px] px-4 border border-neutral-700 text-neutral-400 heading text-[11px] tracking-widest hover:border-red-600 hover:text-red-400 transition-colors">
          SUPPRESS ADDRESS…
        </button>
        <button data-testid="verify-recipients-btn" onClick={verifyRecipients}
          className="min-h-[38px] px-4 border border-neutral-700 text-neutral-400 heading text-[11px] tracking-widest hover:border-volt hover:text-volt transition-colors">
          RE-VERIFY RECIPIENTS
        </button>
        <button data-testid="retemplate-queue-btn" onClick={retemplateQueue}
          className="min-h-[38px] px-4 border border-neutral-700 text-neutral-400 heading text-[11px] tracking-widest hover:border-volt hover:text-volt transition-colors">
          REWRITE QUEUED COPY
        </button>
      </div>

      {!data ? (
        <div className="mt-8 text-neutral-500 text-sm">Loading…</div>
      ) : (
        <>
          {data.kill_switch && (
            <div className="mt-6 border border-red-700 bg-red-950/30 px-4 py-3 text-sm text-red-300" data-testid="kill-switch-banner">
              ⛔ GLOBAL EMAIL PAUSE IS ON — no campaign, nudge, or batch will send anything until it's lifted (Delivery tab → resume sending).
            </div>
          )}
          <div className="mt-6 grid xl:grid-cols-2 gap-4">
            <CampaignCard cid="coach_claim" c={data.coach_claim} toggleUrl="/admin/drip" onChanged={load} />
            <CampaignCard cid="vacancy_outreach" c={data.vacancy_outreach} toggleUrl="/admin/pitch-drip" onChanged={load} />
            {data.monthly_update && <CampaignCard cid="monthly_update" c={data.monthly_update} toggleUrl="/admin/monthly-update" onChanged={load} />}
          </div>
        </>
      )}
    </div>
  );
}

function CampaignCard({ cid, c, toggleUrl, onChanged }) {
  const running = c.status === "running";
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [queuedOpen, setQueuedOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const pause = async () => {
    if (!window.confirm(`Pause "${c.name}"? No more emails will send until you start it again.`)) return;
    setBusy(true);
    try {
      await api.post(`${toggleUrl}/pause`);
      toast.success(`${c.name} paused`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Pause failed"); }
    setBusy(false);
  };

  const sendBatch = async () => {
    const raw = window.prompt(
      "Controlled batch: how many claim invites to send RIGHT NOW?\n\nWorks even while the campaign is paused. Daily cap, unsubscribes, bounces, and the global email pause all still apply.",
      "10"
    );
    const n = parseInt(raw, 10);
    if (!n || n < 1) return;
    if (!window.confirm(`Send ${n} claim invite${n === 1 ? "" : "s"} now?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/drip/send-batch?n=${n}`);
      if (data.blocked) toast.warning(`Batch blocked — ${data.blocked}. Nothing sent.`);
      else toast.success(`Batch: ${data.sent ?? 0} sent${data.failed ? `, ${data.failed} failed` : ""}`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Batch send failed"); }
    setBusy(false);
  };

  const sendTestInvite = async () => {
    const email = window.prompt(
      "Claim-path TEST: send the real production claim email (live claim link) to which address?\n\nUse your own desktop / iPhone / non-Google mailboxes. Test profiles are excluded from analytics and campaigns.");
    if (!email || !email.includes("@")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/claim-test/send", { email });
      toast.success(data.ok ? `Test invite sent to ${data.sent_to} — complete the claim end-to-end on that device` : "Send did not go through — check Delivery settings");
    } catch (e) { toast.error(e.response?.data?.detail || "Test send failed"); }
    setBusy(false);
  };

  const results = c.results || {};
  return (
    <div className={`border bg-appcard p-5 ${running ? "border-emerald-800/60" : "border-amber-700/60"}`} data-testid={`campaign-card-${cid}`}>
      <div className="flex items-center gap-2 flex-wrap">
        {running ? <PlayCircle size={16} className="text-emerald-400" /> : <PauseCircle size={16} className="text-amber-400" />}
        <span className="heading text-lg">{c.name}</span>
        <span className={`ml-auto text-[9px] tracking-widest px-2 py-1 border ${running ? "border-emerald-600 text-emerald-400" : "border-amber-600 text-amber-400"}`} data-testid={`campaign-status-${cid}`}>
          {running ? "RUNNING" : "PAUSED"}
        </span>
      </div>
      <p className="text-xs text-neutral-400 mt-2 leading-relaxed">{c.purpose}</p>
      {!running && c.paused_reason && (
        <div className="mt-2 text-[11px] text-amber-400/90 border border-amber-800/40 bg-amber-950/20 px-2.5 py-1.5" data-testid={`campaign-paused-reason-${cid}`}>
          {c.paused_reason}
        </div>
      )}

      <div className="mt-4 grid grid-cols-3 md:grid-cols-5 gap-2 text-center">
        <button data-testid={`campaign-queued-${cid}`} onClick={() => setQueuedOpen(true)}
          className="border border-neutral-700 p-2 hover:border-volt transition-colors">
          <div className="heading text-xl text-white">{c.queued}</div>
          <div className="text-[9px] tracking-widest text-neutral-500">QUEUED ↗</div>
        </button>
        {[["READY", c.ready], ["BLOCKED", c.blocked], ["SENT TODAY", c.sent_today], ["DAILY LIMIT", c.daily_limit]].map(([l, v]) => (
          <div key={l} className="border border-neutral-800 p-2" title={l === "BLOCKED" ? (cid === "coach_claim" ? "Queued recipients skipped because they unsubscribed or their address bounced" : "Drafted pitches that can't send — missing a recipient email") : undefined}>
            <div className={`heading text-xl ${l === "BLOCKED" && v > 0 ? "text-amber-400" : ""}`}>{v}</div>
            <div className="text-[9px] tracking-widest text-neutral-500">{l}</div>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-2 text-[10px] text-neutral-500">
        <Clock size={11} />
        <span>Last successful run: {c.last_run ? new Date(c.last_run).toLocaleString() : "never"}</span>
        <span>· Next scheduled: {c.next_run ? new Date(c.next_run).toLocaleString() : running ? "—" : "paused"}</span>
      </div>
      {c.retry_exhausted > 0 && (
        <div className="mt-2 text-[11px] text-amber-400/90" data-testid={`campaign-retry-exhausted-${cid}`}>
          ⚠ {c.retry_exhausted} queued record{c.retry_exhausted === 1 ? "" : "s"} exhausted their retries — open QUEUED for details.
        </div>
      )}
      {c.rebalanced_today && (
        <div className="mt-2 text-[11px] text-sky-300/90 border border-sky-800/40 bg-sky-950/20 px-2.5 py-1.5" data-testid={`campaign-rebalanced-${cid}`}>
          ⇄ Quota rebalance active — capped at {c.daily_limit}/day today (normally {c.normal_limit}) so claim invites get first call on the sends.
        </div>
      )}
      {c.click_validation && <ClickValidation v={c.click_validation} cid={cid} />}

      {/* Results */}
      <div className="mt-4 border-t border-neutral-800 pt-3">
        <div className="text-[9px] tracking-widest text-neutral-600">RESULTS TO DATE</div>
        <div className="mt-2 flex flex-wrap gap-2" data-testid={`campaign-results-${cid}`}>
          {Object.entries(results).map(([k, v]) => (
            <span key={k} className="px-2 py-1 border border-neutral-800 text-[10px] tracking-widest text-neutral-300">
              {k.replace(/_/g, " ").toUpperCase()}: <b className="text-white">{v}</b>
            </span>
          ))}
        </div>
      </div>

      <div className="mt-4 flex gap-2">
        {running ? (
          <button data-testid={`campaign-pause-${cid}`} onClick={pause} disabled={busy}
            className="min-h-[42px] px-5 border border-amber-600 text-amber-400 heading text-xs tracking-widest hover:bg-amber-600 hover:text-black transition-colors disabled:opacity-50">
            PAUSE CAMPAIGN
          </button>
        ) : (
          <button data-testid={`campaign-start-${cid}`} onClick={() => setConfirmOpen(true)} disabled={busy}
            className="min-h-[42px] px-5 bg-emerald-600 text-white heading text-xs tracking-widest hover:bg-emerald-500 transition-colors disabled:opacity-50">
            START CAMPAIGN…
          </button>
        )}
        {cid === "coach_claim" && (
          <button data-testid="campaign-send-batch-btn" onClick={sendBatch} disabled={busy}
            className="min-h-[42px] px-5 border border-sky-600 text-sky-400 heading text-xs tracking-widest hover:bg-sky-600 hover:text-white transition-colors disabled:opacity-50">
            SEND CONTROLLED BATCH…
          </button>
        )}
        {cid === "coach_claim" && (
          <button data-testid="campaign-claim-test-btn" onClick={sendTestInvite} disabled={busy}
            className="min-h-[42px] px-5 border border-emerald-600 text-emerald-400 heading text-xs tracking-widest hover:bg-emerald-600 hover:text-white transition-colors disabled:opacity-50">
            SEND TEST INVITE…
          </button>
        )}
      </div>

      {confirmOpen && <StartConfirmModal cid={cid} c={c} toggleUrl={toggleUrl} onClose={() => setConfirmOpen(false)} onStarted={() => { setConfirmOpen(false); onChanged(); }} />}
      {queuedOpen && <QueuedDrawer cid={cid} name={c.name} onClose={() => setQueuedOpen(false)} />}
    </div>
  );
}

function ClickValidation({ v, cid }) {
  const pct = v.raw_clicks ? Math.round((100 * v.scanner_clicks) / v.raw_clicks) : 0;
  return (
    <div className="mt-4 border border-neutral-800 bg-[#0C0C0E] p-3" data-testid={`click-validation-${cid}`}>
      <div className="flex items-center justify-between">
        <div className="text-[9px] tracking-widest text-neutral-500">CLICK VALIDATION — SCANNERS VS. REAL COACHES</div>
        <div className="text-[10px] text-neutral-500">true open rate <b className="text-emerald-400" data-testid="real-click-rate">{v.real_click_rate_pct}%</b> <span className="line-through text-neutral-600">{v.raw_click_rate_pct}%</span></div>
      </div>
      <div className="mt-2 flex h-2 overflow-hidden bg-neutral-900">
        <div className="bg-emerald-500" style={{ width: `${100 - pct}%` }} title="Real coach clicks" />
        <div className="bg-amber-500" style={{ width: `${pct}%` }} title="Security-scanner clicks" />
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-center">
        {[["raw_clicks", "RAW CLICKS", "text-white"], ["scanner_clicks", "SCANNERS", "text-amber-400"], ["real_clicks", "REAL COACHES", "text-emerald-400"]].map(([k, l, tone]) => (
          <div key={k} data-testid={`click-${k}`}>
            <div className={`heading text-lg ${tone}`}>{v[k]}</div>
            <div className="text-[8px] tracking-widest text-neutral-500">{l}</div>
          </div>
        ))}
      </div>
      <div className="mt-2 text-[10px] text-neutral-500">
        Scanner = opened within 90s of a send (Outlook SafeLinks, Barracuda…). {v.scanner_soft_claims_released} scanner soft-claims released back to the pitch pool
        {v.unvalidated > 0 && <> · {v.unvalidated} awaiting validation (hourly)</>}.
      </div>
    </div>
  );
}

function StartConfirmModal({ cid, c, toggleUrl, onClose, onStarted }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/admin/campaigns/${cid}/queued?limit=5`).then(({ data }) => setPreview(data.items)).catch(() => setPreview([]));
  }, [cid]);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${toggleUrl}/start`);
      toast.success(`${c.name} started${data.first_run ? ` — ${data.first_run.sent ?? 0} sent in the first run` : ""}`);
      onStarted();
    } catch (e) { toast.error(e.response?.data?.detail || "Start failed"); setBusy(false); }
  };

  const willSend = Math.min(c.ready, c.daily_limit);
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid={`start-confirm-${cid}`}>
      <div className="bg-[#0B0B0E] border border-emerald-700/60 max-w-lg w-full p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] tracking-widest text-emerald-400">CONFIRM BEFORE STARTING</div>
            <div className="heading text-2xl mt-1">{c.name}</div>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-white p-1"><X size={18} /></button>
        </div>
        <div className="mt-4 space-y-2 text-sm text-neutral-300">
          <Row label="Will send now / today" value={`${willSend} email${willSend === 1 ? "" : "s"} (first run fires immediately on start)`} />
          <Row label="Daily limit" value={`${c.daily_limit} / day, then continues daily on schedule`} />
          <Row label="Queued total" value={c.queued} />
          <Row label="Suppressed recipients excluded" value={`${cid === "coach_claim" ? c.blocked : "unsubscribed + bounced are always skipped"}`} />
          <Row label="Blocked by missing / invalid data" value={c.blocked} />
        </div>
        <div className="mt-4">
          <div className="text-[10px] tracking-widest text-neutral-500 flex items-center gap-1"><Info size={11} /> FIRST 5 RECIPIENTS PREVIEW</div>
          {!preview ? (
            <div className="text-xs text-neutral-500 mt-2">Loading…</div>
          ) : preview.length === 0 ? (
            <div className="text-xs text-neutral-500 mt-2">Nothing queued.</div>
          ) : (
            <div className="mt-2 space-y-1" data-testid="start-preview-list">
              {preview.map((p) => (
                <div key={p.id} className="border border-neutral-800 px-2.5 py-1.5 text-xs flex items-center gap-2 flex-wrap">
                  <span className="text-white">{p.name}</span>
                  <span className="text-neutral-500">{p.organization}</span>
                  <span className="text-neutral-400 ml-auto font-mono text-[10px]">{p.email}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2.5 text-xs tracking-widest text-neutral-400 hover:text-white">CANCEL</button>
          <button data-testid={`start-confirm-btn-${cid}`} onClick={start} disabled={busy}
            className="px-5 py-2.5 bg-emerald-600 text-white heading text-xs tracking-widest hover:bg-emerald-500 disabled:opacity-50">
            {busy ? "STARTING…" : `CONFIRM — START & SEND ${willSend}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 border-b border-neutral-900 pb-1.5">
      <span className="text-neutral-500 text-xs">{label}</span>
      <span className="text-xs text-right">{value}</span>
    </div>
  );
}

function QueuedDrawer({ cid, name, onClose }) {
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState(null);
  const limit = 25;
  useEffect(() => {
    api.get(`/admin/campaigns/${cid}/queued?skip=${skip}&limit=${limit}`).then(({ data }) => setData(data)).catch(() => {});
  }, [cid, skip]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid={`queued-drawer-${cid}`}>
      <div className="flex-1 bg-black/60" onClick={onClose} />
      <div className="w-full max-w-lg bg-[#0B0B0E] border-l border-neutral-800 h-full overflow-y-auto">
        <div className="sticky top-0 bg-[#0B0B0E] border-b border-neutral-800 px-5 py-4 flex items-center justify-between">
          <div>
            <div className="heading text-lg">QUEUED RECORDS</div>
            <div className="text-xs text-neutral-500">{name} · {data?.total ?? "…"} total</div>
          </div>
          <button data-testid="queued-drawer-close" onClick={onClose} className="text-neutral-500 hover:text-white p-1"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-1.5">
          {!data ? <div className="text-neutral-500 text-sm">Loading…</div> : data.items.map((p) => (
            <div key={p.id} className="border border-neutral-800 px-3 py-2 text-xs" data-testid={`queued-item-${p.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-white font-medium">{p.name}</span>
                <span className="text-[9px] tracking-widest text-neutral-600 border border-neutral-800 px-1">{p.priority}</span>
              </div>
              <div className="text-neutral-500 mt-0.5">{p.organization}</div>
              <div className="text-neutral-400 font-mono text-[10px] mt-0.5">{p.email || "no email"}</div>
              {p.wait_reason && (
                <div className={`mt-1 text-[10px] ${/skipped|exhausted/.test(p.wait_reason) ? "text-amber-400" : "text-neutral-500"}`} data-testid={`queued-wait-reason-${p.id}`}>
                  {p.wait_reason}
                </div>
              )}
            </div>
          ))}
          {data && data.total > limit && (
            <div className="flex items-center gap-3 pt-2 text-[11px] text-neutral-500">
              <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} className="border border-neutral-700 px-2.5 py-1.5 disabled:opacity-40"><ChevronLeft size={12} /></button>
              <span>{skip + 1}–{Math.min(skip + limit, data.total)} of {data.total}</span>
              <button disabled={skip + limit >= data.total} onClick={() => setSkip(skip + limit)} className="border border-neutral-700 px-2.5 py-1.5 disabled:opacity-40"><ChevronRight size={12} /></button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
